import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { ShoppingBag, Plus, Minus, Check, X, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useShop } from '../contexts/ShopContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { cartCount, setIsCartOpen } = useShop();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'All' },
    { id: 't-shirt', label: 'T-Shirts' },
    { id: 'hoodie', label: 'Hoodies' },
    { id: 'hat', label: 'Hats' }
  ];

  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              DJ Shadow <span className="text-[#D9F99D]">Merch</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-400">
              Rep the Shadow lifestyle with exclusive merch
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex justify-center gap-2 mb-12 flex-wrap">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`rounded-full px-6 py-2 ${
                  activeCategory === cat.id
                    ? 'bg-[#D9F99D] text-black'
                    : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`category-${cat.id}`}
              >
                {cat.label}
              </Button>
            ))}
          </div>

          {/* Cart Button - Fixed */}
          <div className="fixed bottom-28 right-6 z-30">
            <Button
              onClick={() => setIsCartOpen(true)}
              className="bg-[#D9F99D] text-black hover:bg-[#BEF264] rounded-full w-14 h-14 shadow-lg relative"
              data-testid="cart-button"
            >
              <ShoppingBag className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Button>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="text-center py-12 text-zinc-400">Loading products...</div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product) => (
                <ProductCard 
                  key={product.id} 
                  product={product}
                  onSelect={() => setSelectedProduct(product)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingBag className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">No products available</h3>
              <p className="text-zinc-400">Check back soon for new merch drops!</p>
            </div>
          )}
        </div>
      </section>

      {/* Product Quick View Modal */}
      {selectedProduct && (
        <ProductModal 
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {/* Cart Drawer */}
      <CartDrawer />
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product, onSelect }) => {
  const mainImage = product.images?.[0];
  
  return (
    <div 
      className="glass-card rounded-2xl overflow-hidden group cursor-pointer transform hover:scale-[1.02] transition-all duration-300"
      onClick={onSelect}
      data-testid={`product-card-${product.id}`}
    >
      <div className="aspect-square relative overflow-hidden bg-zinc-900">
        {mainImage ? (
          <img 
            src={mainImage}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag className="w-16 h-16 text-zinc-700" />
          </div>
        )}
        {/* Price Tag */}
        <div className="absolute top-4 right-4 bg-[#D9F99D] text-black px-3 py-1 rounded-full font-bold text-sm">
          ${product.price.toFixed(2)}
        </div>
      </div>
      <div className="p-5">
        <h3 className="font-bold text-lg mb-1">{product.name}</h3>
        <p className="text-zinc-400 text-sm capitalize">{product.category}</p>
        {product.variants?.length > 0 && (
          <div className="flex gap-1 mt-3">
            {[...new Set(product.variants.map(v => v.color))].slice(0, 4).map((color) => (
              <span 
                key={color}
                className="w-4 h-4 rounded-full border border-white/20"
                style={{ backgroundColor: getColorCode(color) }}
                title={color}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Product Modal Component
const ProductModal = ({ product, onClose }) => {
  const { addToCart, setIsCartOpen } = useShop();
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Get unique sizes and colors
  const sizes = [...new Set(product.variants?.map(v => v.size) || [])];
  const colors = [...new Set(product.variants?.map(v => v.color) || [])];

  // Get available variants for selected options
  const getAvailableInventory = () => {
    if (!selectedSize || !selectedColor) return 0;
    const variant = product.variants?.find(v => v.size === selectedSize && v.color === selectedColor);
    return variant?.inventory || 0;
  };

  const handleAddToCart = () => {
    if (!selectedSize || !selectedColor) {
      toast.error('Please select size and color');
      return;
    }
    
    const inventory = getAvailableInventory();
    if (inventory <= 0) {
      toast.error('This variant is out of stock');
      return;
    }

    addToCart(product, { size: selectedSize, color: selectedColor }, quantity);
    toast.success(`Added ${product.name} to cart!`);
    onClose();
    setIsCartOpen(true);
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    if (product.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    if (product.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  // Handle escape key to close image viewer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showImageViewer) {
        setShowImageViewer(false);
      }
    };
    if (showImageViewer) {
      document.addEventListener('keydown', handleKeyDown);
      // Disable pointer events on dialog backdrop when image viewer is open
      const dialogBackdrop = document.querySelector('[data-state="open"][aria-hidden="true"]');
      if (dialogBackdrop) {
        dialogBackdrop.style.pointerEvents = 'none';
      }
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Re-enable pointer events when image viewer closes
      const dialogBackdrop = document.querySelector('[data-state="open"][aria-hidden="true"]');
      if (dialogBackdrop) {
        dialogBackdrop.style.pointerEvents = 'auto';
      }
    };
  }, [showImageViewer]);

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="bg-[#18181B] border-white/10 max-w-2xl p-0 !max-h-[85vh] md:!max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex flex-col md:grid md:grid-cols-2 flex-1 overflow-hidden">
            {/* Image - Clickable for full view */}
            <div 
              className="w-full h-48 md:h-auto md:aspect-square bg-zinc-900 relative flex-shrink-0 cursor-pointer group"
              onClick={() => product.images?.[0] && setShowImageViewer(true)}
              data-testid="product-image-clickable"
            >
              {product.images?.[0] ? (
                <>
                  <img 
                    src={product.images[currentImageIndex] || product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  {/* Zoom indicator */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-3">
                      <ZoomIn className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  {/* Tap to view hint on mobile */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 px-3 py-1 rounded-full text-xs text-white/80 md:hidden">
                    Tap to view full image
                  </div>
                  {/* Image counter if multiple */}
                  {product.images.length > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 rounded-full text-xs text-white">
                      {currentImageIndex + 1} / {product.images.length}
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ShoppingBag className="w-16 h-16 text-zinc-700" />
                </div>
              )}
            </div>

          {/* Details - Scrollable on mobile */}
          <div className="p-4 md:p-6 overflow-y-auto flex-1 min-h-0">
            <DialogHeader>
              <DialogTitle className="text-xl md:text-2xl font-bold">{product.name}</DialogTitle>
            </DialogHeader>
            
            <p className="text-2xl md:text-3xl font-bold text-[#D9F99D] my-3 md:my-4">
              ${product.price.toFixed(2)}
            </p>

            {product.description && (
              <p className="text-zinc-400 text-sm mb-4 md:mb-6">{product.description}</p>
            )}

            {/* Size Selection */}
            {sizes.length > 0 && (
              <div className="mb-3 md:mb-4">
                <label className="text-sm text-zinc-400 mb-2 block">Size</label>
                <div className="flex gap-2 flex-wrap">
                  {sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg border transition-all text-sm ${
                        selectedSize === size
                          ? 'border-[#D9F99D] bg-[#D9F99D]/10 text-[#D9F99D]'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {colors.length > 0 && (
              <div className="mb-3 md:mb-4">
                <label className="text-sm text-zinc-400 mb-2 block">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`w-8 h-8 md:w-10 md:h-10 rounded-full border-2 transition-all flex items-center justify-center ${
                        selectedColor === color
                          ? 'border-[#D9F99D] ring-2 ring-[#D9F99D]/50'
                          : 'border-white/20 hover:border-white/40'
                      }`}
                      style={{ backgroundColor: getColorCode(color) }}
                      title={color}
                      data-testid={`color-${color}`}
                    >
                      {selectedColor === color && (
                        <Check className={`w-4 h-4 md:w-5 md:h-5 ${color === 'white' ? 'text-black' : 'text-white'}`} />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Inventory Status */}
            {selectedSize && selectedColor && (
              <p className={`text-sm mb-3 ${getAvailableInventory() > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {getAvailableInventory() > 0 
                  ? `${getAvailableInventory()} in stock`
                  : 'Out of stock'}
              </p>
            )}

            {/* Quantity */}
            <div className="mb-4 md:mb-6">
              <label className="text-sm text-zinc-400 mb-2 block">Quantity</label>
              <div className="flex items-center gap-3 md:gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="bg-white/5 border-white/10 h-8 w-8 md:h-10 md:w-10"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-lg md:text-xl font-bold w-8 text-center">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(q => Math.min(getAvailableInventory() || 10, q + 1))}
                  className="bg-white/5 border-white/10 h-8 w-8 md:h-10 md:w-10"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <Button
              onClick={handleAddToCart}
              disabled={!selectedSize || !selectedColor || getAvailableInventory() <= 0}
              className="w-full bg-[#D9F99D] text-black hover:bg-[#BEF264] py-4 md:py-6 rounded-xl font-bold disabled:opacity-50 sticky bottom-0"
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag className="w-5 h-5 mr-2" />
              Add to Cart - ${(product.price * quantity).toFixed(2)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Full Image Viewer - rendered via portal to escape Dialog */}
    {showImageViewer && product.images?.length > 0 && ReactDOM.createPortal(
      <div 
        className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
        onClick={() => setShowImageViewer(false)}
        onTouchEnd={(e) => {
          if (e.target === e.currentTarget) {
            setShowImageViewer(false);
          }
        }}
        data-testid="image-viewer-overlay"
      >
        {/* Close button - positioned below safe area for iOS notch */}
        <button 
          className="absolute top-16 right-4 z-10 bg-white/20 hover:bg-white/30 active:bg-white/40 rounded-full p-4 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            setShowImageViewer(false);
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            setShowImageViewer(false);
          }}
          data-testid="image-viewer-close"
        >
          <X className="w-8 h-8 text-white" />
        </button>

        {/* Bottom close button for easier mobile access */}
        <button 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 bg-white/20 hover:bg-white/30 active:bg-white/40 rounded-full px-6 py-3 transition-colors flex items-center gap-2"
          onClick={(e) => {
            e.stopPropagation();
            setShowImageViewer(false);
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            setShowImageViewer(false);
          }}
          data-testid="image-viewer-close-bottom"
        >
          <X className="w-5 h-5 text-white" />
          <span className="text-white font-medium">Close</span>
        </button>

        {/* Image container */}
        <div 
          className="relative max-w-[90vw] max-h-[70vh] flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img 
            src={product.images[currentImageIndex]}
            alt={product.name}
            className="max-w-full max-h-[65vh] object-contain rounded-lg"
            data-testid="image-viewer-img"
          />

          {/* Navigation arrows for multiple images */}
          {product.images.length > 1 && (
            <>
              <button 
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 rounded-full p-2 transition-colors"
                onClick={handlePrevImage}
                data-testid="image-viewer-prev"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button 
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 rounded-full p-2 transition-colors"
                onClick={handleNextImage}
                data-testid="image-viewer-next"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </>
          )}
        </div>

        {/* Image counter and thumbnails */}
        {product.images.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
            {/* Thumbnails */}
            <div className="flex gap-2">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(idx);
                  }}
                  className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                    currentImageIndex === idx ? 'border-[#D9F99D]' : 'border-white/20 hover:border-white/40'
                  }`}
                  data-testid={`image-viewer-thumb-${idx}`}
                >
                  <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            {/* Counter */}
            <div className="text-white/80 text-sm">
              {currentImageIndex + 1} / {product.images.length}
            </div>
          </div>
        )}

        {/* Product name - positioned below safe area */}
        <div className="absolute top-16 left-4 text-white font-bold text-lg bg-black/60 px-4 py-2 rounded-lg max-w-[60%] truncate">
          {product.name}
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

// Cart Drawer Component
const CartDrawer = () => {
  const { cart, cartTotal, isCartOpen, setIsCartOpen, removeFromCart, updateQuantity } = useShop();
  
  // Calculate shipping
  const shippingCost = cartTotal >= 50 ? 0 : 5.99;
  const total = cartTotal + shippingCost;

  if (!isCartOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => setIsCartOpen(false)}
      />
      
      {/* Drawer */}
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#18181B] border-l border-white/10 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#D9F99D]" />
            Your Cart
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsCartOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cart.length > 0 ? (
            <div className="space-y-4">
              {cart.map((item, idx) => (
                <div key={`${item.product_id}-${item.size}-${item.color}-${idx}`} className="flex gap-4 bg-white/5 rounded-xl p-4">
                  {/* Image */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-zinc-900 flex-shrink-0">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-6 h-6 text-zinc-700" />
                      </div>
                    )}
                  </div>
                  
                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold truncate">{item.name}</h4>
                    <p className="text-sm text-zinc-400">{item.size} / {item.color}</p>
                    <p className="text-[#D9F99D] font-bold">${item.price.toFixed(2)}</p>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="w-7 h-7 bg-white/5 border-white/10"
                        onClick={() => updateQuantity(item.product_id, item.size, item.color, item.quantity - 1)}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-6 text-center text-sm">{item.quantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="w-7 h-7 bg-white/5 border-white/10"
                        onClick={() => updateQuantity(item.product_id, item.size, item.color, item.quantity + 1)}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Remove */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFromCart(item.product_id, item.size, item.color)}
                    className="text-red-400 hover:bg-red-500/10 self-start"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingBag className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">Your cart is empty</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="p-6 border-t border-white/10 space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Subtotal</span>
                <span>${cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Shipping</span>
                <span>{shippingCost === 0 ? <span className="text-green-400">FREE</span> : `$${shippingCost.toFixed(2)}`}</span>
              </div>
              {cartTotal < 50 && (
                <p className="text-xs text-zinc-500">Free shipping on orders over $50</p>
              )}
              <div className="flex justify-between font-bold text-lg pt-2 border-t border-white/10">
                <span>Total</span>
                <span className="text-[#D9F99D]">${total.toFixed(2)}</span>
              </div>
            </div>
            <Link to="/shop/cart" onClick={() => setIsCartOpen(false)}>
              <Button className="w-full bg-[#D9F99D] text-black hover:bg-[#BEF264] py-6 rounded-xl font-bold" data-testid="checkout-btn">
                Proceed to Checkout
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to get color code
const getColorCode = (colorName) => {
  const colors = {
    black: '#000000',
    white: '#ffffff',
    red: '#ef4444',
    blue: '#3b82f6',
    green: '#22c55e',
    yellow: '#eab308',
    orange: '#f97316',
    purple: '#a855f7',
    pink: '#ec4899',
    gray: '#6b7280',
    navy: '#1e3a5f',
    maroon: '#7f1d1d'
  };
  return colors[colorName.toLowerCase()] || '#6b7280';
};

export default Shop;
